import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay } from "@dnd-kit/core";
import { toast } from "sonner";
import { Lock, Unlock, Save, GripVertical, BookMarked, FileText, Briefcase, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useMe } from "@/lib/me";
import { PAGE_TYPES, pageType, CHECKLIST, AD_PAGE_TYPES, dealStageLabel } from "@/lib/constants";
import { PageHeader, Pill, Metric } from "@/components/app/shared";
import { cn } from "@/lib/utils";

const pagesLabel = (s) =>
  s.leftPageNumber && s.rightPageNumber ? `${s.leftPageNumber}–${s.rightPageNumber}` : `${s.leftPageNumber || s.rightPageNumber}`;
const ckCount = (s) => CHECKLIST.filter((c) => s.checklist?.[c.id]).length;

function Half({ type, label, num, side }) {
  const pt = pageType(type);
  if (!type) return <div className="flex-1 bg-surface-3" />;
  return (
    <div className={cn("relative flex flex-1 flex-col justify-between p-2", pt.cls, side === "left" && "border-r border-border")}>
      <div className="truncate text-[10.5px] font-medium leading-tight">{label || (type === "EMPTY" ? "" : pt.short)}</div>
      <div className={cn("flex items-end justify-between font-mono text-[9.5px]", side === "right" && "flex-row-reverse")}>
        <span className="shrink-0">{num}</span>
        <span className="truncate pl-1 opacity-60">{type === "EMPTY" ? "libre" : pt.short}</span>
      </div>
    </div>
  );
}

function SpreadCard({ s, sigStart, onOpen, fixed, overlay }) {
  const { attributes, listeners, setNodeRef: dragRef, isDragging } = useDraggable({ id: s.id, disabled: fixed || overlay });
  const { setNodeRef: dropRef, isOver } = useDroppable({ id: s.id, disabled: fixed || overlay });
  const n = ckCount(s);
  return (
    <div ref={dropRef} className={cn("relative", sigStart && "pl-2")} data-testid="flatplan-spread">
      {sigStart && <div className="absolute bottom-1 left-0 top-1 w-[3px] rounded bg-primary" />}
      <div
        ref={dragRef}
        className={cn(
          "group overflow-hidden rounded-md border bg-card transition-colors",
          isOver ? "border-primary ring-2 ring-primary/50" : "border-border hover:border-foreground/40",
          isDragging && "opacity-30"
        )}
      >
        <button onClick={onOpen} className="flex aspect-[2/1.15] w-full text-left" data-testid={`flatplan-spread-${s.spreadNumber}`}>
          <Half type={s.leftPageType} label={s.leftLabel} num={s.leftPageNumber} side="left" />
          <Half type={s.rightPageType} label={s.rightLabel} num={s.rightPageNumber} side="right" />
        </button>
        <div className="flex items-center justify-between border-t border-border px-2 py-1">
          <div className="flex items-center gap-1.5">
            {!fixed ? (
              <span {...attributes} {...listeners} className="cursor-grab text-muted-foreground active:cursor-grabbing" aria-label="Arrastrar"><GripVertical className="h-3.5 w-3.5" /></span>
            ) : (
              <Lock className={cn("h-3 w-3", s.isLocked ? "text-foreground" : "text-muted-foreground")} />
            )}
            <span className="font-mono text-[10px] text-muted-foreground">[{pagesLabel(s)}]</span>
          </div>
          <div className="flex items-center gap-1">
            {s.story && <FileText className="h-3 w-3" />}
            {s.deal && <Briefcase className="h-3 w-3" />}
            <div className="flex gap-[2px]" title={`${n}/4 checklist`}>
              {[0, 1, 2, 3].map((i) => <span key={i} className={cn("h-1.5 w-1.5 rounded-full", i < n ? (n === 4 ? "bg-[hsl(var(--status-ok))]" : "bg-primary") : "bg-border")} />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpreadSheet({ spread, setSpread, book, role }) {
  const qc = useQueryClient();
  const [f, setF] = useState(null);
  const [radarPick, setRadarPick] = useState("");
  useEffect(() => { setF(spread ? { ...spread } : null); setRadarPick(""); }, [spread]);
  const save = useMutation({
    mutationFn: async (body) => (await api.patch(`/book/spreads/${spread.id}`, body)).data,
    onSuccess: (s) => { qc.invalidateQueries({ queryKey: ["book"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); setSpread({ ...spread, ...s }); toast.success("Pliego actualizado"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const assignRadar = useMutation({
    mutationFn: async () => (await api.post(`/book/spreads/${spread.id}/assign-radar`, { radarItemId: radarPick })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["book"] }); qc.invalidateQueries({ queryKey: ["stories"] }); setSpread(null); toast.success("Historia creada y asignada al pliego"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!f) return null;
  const isCO = role === "COMMERCIAL";
  const isED = role === "EDITORIAL";
  const locked = spread.isLocked;
  const typesFor = (cur) => (isCO ? (AD_PAGE_TYPES.includes(cur) ? PAGE_TYPES.filter((p) => AD_PAGE_TYPES.includes(p.id)) : [pageType(cur)]) : PAGE_TYPES.filter((p) => p.id !== "COVER" || cur === "COVER"));
  const storyOptions = [...(spread.story ? [spread.story] : []), ...book.backlog.stories];
  const submit = () => {
    const body = {};
    if (!isCO) Object.assign(body, { leftLabel: f.leftLabel, rightLabel: f.rightLabel, notes: f.notes, checklist: f.checklist, storyId: f.storyId || null });
    if (isCO) Object.assign(body, { leftLabel: f.leftLabel, rightLabel: f.rightLabel, notes: f.notes });
    if (f.leftPageType && f.leftPageType !== spread.leftPageType) body.leftPageType = f.leftPageType;
    if (f.rightPageType && f.rightPageType !== spread.rightPageType) body.rightPageType = f.rightPageType;
    if (!isED && (f.dealId || null) !== (spread.dealId || null)) body.dealId = f.dealId || null;
    if (!isED && body.dealId === undefined) delete body.dealId;
    if (body.storyId === (spread.storyId || null)) delete body.storyId;
    if (locked) { delete body.storyId; delete body.dealId; delete body.leftPageType; delete body.rightPageType; }
    save.mutate(body);
  };
  return (
    <Sheet open={!!spread} onOpenChange={(o) => !o && setSpread(null)}>
      <SheetContent className="w-full overflow-y-auto bg-card sm:max-w-lg" data-testid="spread-sheet">
        <SheetHeader>
          <div className="kicker">Pliego {spread.spreadNumber}</div>
          <SheetTitle className="font-display text-5xl font-normal">Pág. {pagesLabel(spread)}</SheetTitle>
          <SheetDescription>{locked ? "Bloqueado: el contenido y la posición están fijados para imprenta." : "Define el tipo de cada página, el contenido y su estado para imprenta."}</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-5">
          {!isCO && (
            <label className="flex items-center justify-between rounded-md border border-border px-3 py-2.5 text-sm">
              <span className="flex items-center gap-2">{locked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />} Pliego bloqueado</span>
              <Switch checked={locked} onCheckedChange={(v) => save.mutate({ isLocked: v })} data-testid="spread-lock-switch" />
            </label>
          )}
          <div className="grid grid-cols-2 gap-3">
            {[["left", "Izquierda", spread.leftPageNumber], ["right", "Derecha", spread.rightPageNumber]].map(([side, lbl, num]) =>
              f[`${side}PageType`] ? (
                <div key={side} className="space-y-2 rounded-md border border-border p-3">
                  <div className="kicker">{lbl} · p.{num}</div>
                  <Select value={f[`${side}PageType`]} onValueChange={(v) => setF({ ...f, [`${side}PageType`]: v })} disabled={locked || f[`${side}PageType`] === "COVER"}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`spread-${side}-type-select`}><SelectValue /></SelectTrigger>
                    <SelectContent>{typesFor(spread[`${side}PageType`]).map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={f[`${side}Label`] || ""} onChange={(e) => setF({ ...f, [`${side}Label`]: e.target.value })} placeholder="Etiqueta" className="h-8 text-xs" data-testid={`spread-${side}-label-input`} />
                </div>
              ) : (
                <div key={side} className="flex items-center justify-center rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">Sin página</div>
              )
            )}
          </div>
          {!isCO && (
            <div className="grid gap-1.5">
              <Label className="text-xs">Historia editorial</Label>
              <Select value={f.storyId || "none"} onValueChange={(v) => setF({ ...f, storyId: v === "none" ? null : v })} disabled={locked}>
                <SelectTrigger data-testid="spread-story-select"><SelectValue placeholder="Sin historia" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin historia</SelectItem>
                  {storyOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                </SelectContent>
              </Select>
              {book.backlog.radar.length > 0 && !locked && (
                <div className="mt-1 flex gap-2">
                  <Select value={radarPick} onValueChange={setRadarPick}>
                    <SelectTrigger className="h-8 text-xs" data-testid="spread-radar-select"><SelectValue placeholder="O desde el backlog del Radar…" /></SelectTrigger>
                    <SelectContent>{book.backlog.radar.map((r) => <SelectItem key={r.id} value={r.id}>{r.suggestedTitle}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" disabled={!radarPick || assignRadar.isPending} onClick={() => assignRadar.mutate()} data-testid="spread-assign-radar-button">Asignar</Button>
                </div>
              )}
            </div>
          )}
          {!isED && (
            <div className="grid gap-1.5">
              <Label className="text-xs">Anunciante (pauta en libro)</Label>
              <Select value={f.dealId || "none"} onValueChange={(v) => setF({ ...f, dealId: v === "none" ? null : v })} disabled={locked}>
                <SelectTrigger data-testid="spread-deal-select"><SelectValue placeholder="Sin anunciante" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin anunciante</SelectItem>
                  {book.bookDeals.map((d) => <SelectItem key={d.id} value={d.id}>{d.clientName} · {dealStageLabel(d.stage)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {isED && spread.deal && <div className="text-xs text-muted-foreground">Anunciante: <b className="text-foreground">{spread.deal.clientName}</b></div>}
          {!isCO && (
            <div className="rounded-md border border-border p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="kicker">Checklist de imprenta</div>
                <span className="font-mono text-[11px]">{CHECKLIST.filter((c) => f.checklist?.[c.id]).length}/4</span>
              </div>
              <div className="space-y-2">
                {CHECKLIST.map((c) => (
                  <label key={c.id} className="flex cursor-pointer items-center gap-2.5 text-sm">
                    <Checkbox checked={!!f.checklist?.[c.id]} onCheckedChange={(v) => setF({ ...f, checklist: { ...f.checklist, [c.id]: !!v } })} data-testid={`spread-check-${c.id}`} />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="grid gap-1.5">
            <Label className="text-xs">Notas</Label>
            <Textarea rows={3} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="spread-notes-input" />
          </div>
          <Button className="w-full" onClick={submit} disabled={save.isPending} data-testid="spread-save-button">
            {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar pliego
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Flatplan() {
  const me = useMe();
  const qc = useQueryClient();
  const [open, setOpen] = useState(null);
  const [active, setActive] = useState(null);
  const { data: book, isLoading } = useQuery({ queryKey: ["book"], queryFn: async () => (await api.get("/book")).data });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const canConfig = me.role !== "COMMERCIAL";

  const reorder = useMutation({
    mutationFn: async (orderedIds) => (await api.post("/book/reorder", { orderedIds })).data,
    onMutate: async (orderedIds) => {
      const prev = qc.getQueryData(["book"]);
      return { prev };
    },
    onSuccess: (b) => { qc.setQueryData(["book"], b); toast.success("Pliegos intercambiados"); },
    onError: (e, _v, ctx) => { qc.setQueryData(["book"], ctx.prev); toast.error(errMsg(e)); },
  });
  const config = useMutation({
    mutationFn: async (body) => (await api.patch("/book/config", body)).data,
    onSuccess: (b) => { qc.setQueryData(["book"], b); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Configuración del libro actualizada"); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const spreads = book?.spreads || [];
  const sig = book?.config?.signatureSize || 16;
  const n = spreads.length;
  const isFixed = (s) => s.isLocked || s.position === 0 || s.position === n - 1;
  const sigOf = (s) => Math.ceil((s.leftPageNumber || s.rightPageNumber) / sig);
  const totalOptions = useMemo(() => { const o = []; for (let p = Math.ceil(200 / sig) * sig; p <= 280; p += sig) o.push(p); return o; }, [sig]);

  const onDragEnd = (e) => {
    setActive(null);
    if (!e.over || e.over.id === e.active.id) return;
    const a = spreads.find((s) => s.id === e.active.id);
    const b = spreads.find((s) => s.id === e.over.id);
    if (!a || !b || isFixed(a) || isFixed(b)) return toast.error("Los pliegos bloqueados y las portadas no se mueven");
    const ids = spreads.map((s) => s.id);
    ids[a.position] = b.id;
    ids[b.position] = a.id;
    reorder.mutate(ids);
  };

  if (isLoading || !book) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-[600px]" /></div>;
  const st = book.stats;
  return (
    <div className="space-y-6">
      <PageHeader
        kicker={`04 · Print Room · entrega a imprenta ${fmtDate(book.config.printDeadline, { day: "numeric", month: "long", year: "numeric" })}`}
        title="Flatplan Libro Anual"
        subtitle={`${book.config.title}. Arrastra un pliego sobre otro para intercambiarlos y equilibrar el ritmo y los compromisos comerciales.`}
        actions={
          canConfig && (
            <div className="flex items-center gap-2">
              <Select value={String(sig)} onValueChange={(v) => config.mutate({ signatureSize: Number(v), totalPages: book.config.totalPages })}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="flatplan-signature-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="8">Firmas de 8 pág.</SelectItem><SelectItem value="16">Firmas de 16 pág.</SelectItem></SelectContent>
              </Select>
              <Select value={String(book.config.totalPages)} onValueChange={(v) => config.mutate({ totalPages: Number(v) })}>
                <SelectTrigger className="h-9 w-[130px]" data-testid="flatplan-total-select"><SelectValue /></SelectTrigger>
                <SelectContent>{totalOptions.map((t) => <SelectItem key={t} value={String(t)}>{t} páginas</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )
        }
      />
      <div className="grid gap-4 md:grid-cols-4">
        <Metric testid="flatplan-locked" label="Páginas bloqueadas" value={`${st.lockedPages}/${st.totalPages}`} hint={`${st.percent}% listo para imprenta`}><Progress value={st.percent} className="mt-3 h-1" /></Metric>
        <Metric label="Páginas planificadas" value={st.plannedPages} hint="con tipo de contenido asignado" />
        <Metric label="Páginas de pauta" value={st.adPages} hint="anuncios simples y dobles" />
        <Metric label="Firmas" value={Math.ceil(st.totalPages / sig)} hint={`de ${sig} páginas cada una`} />
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-4 py-3" data-testid="flatplan-legend">
        {PAGE_TYPES.map((p) => (
          <div key={p.id} className="flex items-center gap-1.5 text-[11px]">
            <span className={cn("h-3.5 w-5 rounded-sm border border-border", p.cls)} /> {p.label}
          </div>
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-[1fr_280px]">
        <DndContext sensors={sensors} onDragStart={(e) => setActive(spreads.find((s) => s.id === e.active.id))} onDragCancel={() => setActive(null)} onDragEnd={onDragEnd}>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(168px,1fr))] gap-x-3 gap-y-4" data-testid="flatplan-grid">
            {spreads.map((s, i) => {
              const sigStart = i === 0 || sigOf(s) !== sigOf(spreads[i - 1]);
              return (
                <div key={s.id}>
                  <div className="mb-1 h-3 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">{sigStart ? `Firma ${sigOf(s)}` : ""}</div>
                  <SpreadCard s={s} sigStart={sigStart} fixed={isFixed(s)} onOpen={() => setOpen(s)} />
                </div>
              );
            })}
          </div>
          <DragOverlay>{active ? <div className="w-[180px] rotate-1 shadow-lg"><SpreadCard s={active} overlay /></div> : null}</DragOverlay>
        </DndContext>
        <aside className="space-y-4">
          <section className="rounded-lg border border-border bg-card" data-testid="print-backlog">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <BookMarked className="h-4 w-4" strokeWidth={1.75} />
              <div className="text-sm font-semibold">Backlog de impresión</div>
            </div>
            <div className="max-h-[520px] divide-y divide-border overflow-y-auto">
              {book.backlog.stories.length + book.backlog.radar.length === 0 && <div className="p-4 text-xs text-muted-foreground">Nada pendiente. Marca pitches del Radar o historias como elegibles para el libro.</div>}
              {book.backlog.stories.map((s) => (
                <div key={s.id} className="px-4 py-2.5">
                  <div className="text-[13px] leading-snug">{s.title}</div>
                  <div className="mt-1 flex gap-1"><Pill className="text-[10px]">Historia</Pill><Pill className="text-[10px]">{s.section}</Pill></div>
                </div>
              ))}
              {book.backlog.radar.map((r) => (
                <div key={r.id} className="px-4 py-2.5">
                  <div className="text-[13px] leading-snug">{r.suggestedTitle}</div>
                  <div className="mt-1 flex gap-1"><Pill className="text-[10px]">Radar</Pill><Pill className="text-[10px]">{r.section}</Pill></div>
                </div>
              ))}
            </div>
            <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">Abre un pliego para asignarle un elemento del backlog.</div>
          </section>
        </aside>
      </div>
      <SpreadSheet spread={open} setSpread={setOpen} book={book} role={me.role} />
    </div>
  );
}
